import React, { useEffect } from 'react';
import '../scss/Roadmap.scss';
import { StaticHeader, StaticFooter } from '../components';

const Roadmap = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const roadmapData = [
    {
      category: 'Additional Board Views',
      status: 'in-progress',
      items: [
        { name: 'List View', status: 'in-progress' },
        { name: 'Calendar View (monthly, weekly, daily)', status: 'planned' },
        { name: 'Table / Spreadsheet View', status: 'planned' },
        { name: 'Gantt Charts', status: 'planned' },
      ]
    },
    {
      category: 'Task Management',
      status: 'planned',
      items: [
        { name: 'Task templates', status: 'planned' },
        { name: 'Board templates', status: 'planned' },
        { name: 'Panner', status: 'planned' },
      ]
    },
    {
      category: 'Automations',
      status: 'planned',
      items: [
        { name: 'Auto-assign tasks based on rules', status: 'planned' },
        { name: 'Due date automations', status: 'planned' },
        { name: 'Recurring actions', status: 'planned' },
        { name: 'Auto-status updates', status: 'planned' },
        { name: 'Scheduled automations', status: 'planned' },
      ]
    },
    {
      category: 'Imports & Migration',
      status: 'in-progress',
      items: [
        { name: 'Import from Trello', status: 'completed' },
        { name: 'Import from ClickUp', status: 'planned' },
        { name: 'Import from Notion', status: 'planned' },
        { name: 'Import from Linear', status: 'planned' },
        { name: 'Import from Asana', status: 'planned' },
        { name: 'Import from Jira', status: 'planned' },
        { name: 'Import from WordPress', status: 'completed' },
        { name: 'CSV import & export', status: 'planned' },
      ]
    },
    {
      category: 'Integration',
      status: 'in-progress',
      items: [
        { name: 'Slack', status: 'completed' },
        { name: 'Google Drive', status: 'planned' },
        { name: 'Dropbox', status: 'planned' },
        { name: 'OneDrive', status: 'planned' },
        { name: 'Zapier', status: 'planned' },
        { name: 'Make.com', status: 'planned' },
        { name: 'n8n', status: 'planned' },
      ]
    },
    {
      category: 'Documentation',
      status: 'planned',
      items: [
        { name: 'Pages/Docs', status: 'in-progress' },
        { name: 'SOP', status: 'planned' },
        { name: 'Web publishing', status: 'planned' },
      ]
    },
    {
      category: 'Extras',
      status: 'planned',
      items: [
        { name: 'Export/Backup extension', status: 'planned' },
        { name: 'Calendar sync extension', status: 'planned' },
        { name: 'Email-to-board', status: 'planned' },
        { name: 'Extension marketplace', status: 'planned' },
      ]
    },
    {
      category: 'Mobile App Plan',
      status: 'planned',
      items: [
        { name: 'iOS app', status: 'planned' },
        { name: 'Android app', status: 'planned' },
      ]
    },
  ];

  const getStatusBadge = (status) => {
    const badges = {
      'completed': { label: 'Completed', className: 'status-completed' },
      'in-progress': { label: 'In Progress', className: 'status-in-progress' },
      'planned': { label: 'Planned', className: 'status-planned' },
    };
    return badges[status] || badges['planned'];
  };

  return (
    <div className="roadmap-page">
      {/* Navigation */}
      <StaticHeader />

      {/* Hero Section */}
      <section className="roadmap-hero">
        <div className="hero__container">
          <h1 className="hero__title">
            🚀 <span className="hero__title-accent">Zoobbe</span> Roadmap
          </h1>
          <p className="hero__subtitle">
            See what we're building next. Your feedback shapes our future.
          </p>
        </div>
      </section>

      {/* Legend Section */}
      <section className="roadmap-legend">
        <div className="container">
          <div className="legend-items">
            <div className="legend-item">
              <span className="status-badge status-completed">Completed</span>
              <span className="legend-description">Already available</span>
            </div>
            <div className="legend-item">
              <span className="status-badge status-in-progress">In Progress</span>
              <span className="legend-description">Currently being developed</span>
            </div>
            <div className="legend-item">
              <span className="status-badge status-planned">Planned</span>
              <span className="legend-description">Coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Content */}
      <section className="roadmap-content">
        <div className="container">
          <div className="roadmap-grid">
            {roadmapData.map((category, index) => (
              <div key={index} className="roadmap-category">
                <div className="category-header">
                  <h3 className="category-title">{category.category}</h3>
                  <span className={`status-badge ${getStatusBadge(category.status).className}`}>
                    {getStatusBadge(category.status).label}
                  </span>
                </div>
                <ul className="category-items">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="category-item">
                      <span className={`item-indicator ${getStatusBadge(item.status).className}`}></span>
                      <span className="item-name">{item.name}</span>
                      <span className={`item-status ${getStatusBadge(item.status).className}`}>
                        {getStatusBadge(item.status).label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section className="roadmap-feedback">
        <div className="container">
          <div className="feedback-content">
            <h2>Have a Feature Request?</h2>
            <p>We'd love to hear your ideas! Your feedback helps us prioritize what to build next.</p>
            <div className="feedback-actions">
              <a href="/feature-request" className="feedback-button primary">Submit Feature Request</a>
              <a href="/contact" className="feedback-button secondary">Contact Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of teams already using Zoobbe to stay organized and productive.</p>
            <div className="cta-actions">
              <a href="/signup" className="cta-button primary">Get Started Free</a>
              <a href="/features-comparison" className="cta-button secondary">View Features</a>
            </div>
            {/* <p className="cta-note">No credit card required • Free forever plan available</p> */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <StaticFooter />
    </div>
  );
};

export default Roadmap;

